import { useContext } from "react";
import { trackRequest } from "./motion.js";
export * from "./theme.js";
export * from "./components/index.js";
export function defineWebModule(m) {
    return m;
}
export { ShellContext } from "./context.js";
import { ShellContext } from "./context.js";
/** The signed-in session, navigation and permission check, inside any page. */
export function useShell() {
    const ctx = useContext(ShellContext);
    if (!ctx)
        throw new Error("useShell() must be used inside the app shell");
    return ctx;
}
/** An API error: the message to show, plus the server's code (e.g. "step-up", "need-2fa") and status. */
export class ApiError extends Error {
    status;
    code;
    data;
    constructor(message, status, code, data) {
        super(message);
        this.status = status;
        this.code = code;
        this.data = data;
    }
}
/**
 * "Confirm it's you": the app shell registers a handler that asks for Face ID, the PIN or password + code. When a
 * request comes back needing it, api() asks, then retries the request once — so every page gets it for free.
 */
let confirmHandler = null;
export function onConfirmNeeded(h) { confirmHandler = h; }
/** JSON fetch with the platform's error shape: throws ApiError(message) on failure. */
export function api(path, init, retried = false) {
    return trackRequest(request(path, init, retried));
}
async function request(path, init, retried = false) {
    const res = await fetch(path, {
        method: init?.method ?? (init?.body === undefined ? "GET" : "POST"),
        headers: init?.body === undefined ? undefined : { "content-type": "application/json" },
        body: init?.body === undefined ? undefined : JSON.stringify(init.body),
        credentials: "same-origin",
    });
    const data = (await res.json().catch(() => ({})));
    if (!res.ok) {
        if (res.status === 403 && data.code === "step-up" && confirmHandler && !retried && !path.startsWith("/api/auth/")) {
            if (await confirmHandler(data.error ?? ""))
                return api(path, init, true);
        }
        throw new ApiError(data.error ?? `Request failed (${res.status})`, res.status, data.code, data);
    }
    return data;
}
/** Upload one file for a module (and site). The server checks the real file type and your access. */
export async function uploadFile(module, site, file) {
    const form = new FormData();
    form.append("module", module);
    if (site)
        form.append("site", site);
    form.append("file", file);
    const res = await fetch("/api/files", { method: "POST", body: form, credentials: "same-origin" });
    const data = (await res.json().catch(() => ({})));
    if (!res.ok)
        throw new Error(data.error ?? "Upload failed");
    return data;
}
export { FilePicker, Thumbs, Tabs, EmptyState, Skeleton, Loading, PageSkeleton, CountUp, Roll } from "./components/extra.js";
export { Icon, iconNames } from "./components/icons.js";
export { NozlDrop, NozlIcon, NozlLogo, PoweredByNozl } from "./components/brand.js";
export { Sheet, AllClear, lockScroll } from "./components/sheet.js";
export { RowSheet, RowEdit, CloseButton, useRowClose, rememberOpener } from "./components/rowsheet.js";
export { BringInHistory } from "./components/history.js";
export { SitePicker, SiteSwitch, SiteList, useSiteChoice, readSites, saveSites, sitesLabel } from "./components/sitepicker.js";
export { ProductImage } from "./components/productimage.js";
export { CompanyLogo, prepareLogo } from "./components/logo.js";
export { Avatar, avatarSrc, avatarSvg, AVATAR_IDS } from "./components/avatars.js";
export { QuickActions, ForecourtScene } from "./components/actions.js";
export { transition, whenSettled, afterTransitions, installGlow, fadeAfter } from "./motion.js";
export { Delta, Sparkline, StatCard, RingGauge, Donut, Columns, League, ActionTiles, Timeline, GradeBadge, gradeName, MetricStrip, greeting, fmt } from "./components/charts.js";
/** UK dates everywhere in the UI: 09/10/26. Accepts "2026-10-09", a full ISO time or a Date. */
export function ukDate(v) {
    if (!v)
        return "—";
    const d = typeof v === "string" ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T12:00:00Z` : v) : v;
    if (Number.isNaN(d.getTime()))
        return String(v);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit", timeZone: "Europe/London" });
}
/** 09/10/26 14:05, UK time. */
export function ukDateTime(v) {
    if (!v)
        return "—";
    const d = typeof v === "string" ? new Date(v) : v;
    return `${ukDate(d)} ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" })}`;
}
/** "2026-04-01" to "2027-03-31" → "01/04/26 – 31/03/27". */
export const ukRange = (a, b) => `${ukDate(a)} – ${ukDate(b)}`;
/** How a site is run, in words. */
export const OPERATING_MODEL_SHORT = { commission: "Commission", operator: "Operator managed", company: "Company" };
export const OPERATING_MODEL_LABEL = { commission: "Commission operator", operator: "Operator managed (weekly fee)", company: "Company managed" };
export { InvoiceDocument } from "./components/invoice.js";
